/**
 * bridge/keep_awake_controller.js — "keep-awake" bridge component (Stimulus + Strada).
 *
 * Identifier: bridge--keep-awake (glob-derived from this subdir; wire with
 * data-controller="bridge--keep-awake"). static component = "keep-awake" MUST equal
 * the iOS KeepAwakeComponent `class var name` and the Android-reached method base
 * (window.ChippieNative.setKeepAwake).
 *
 * Custom-tier bridge component: holds the screen awake during a hands-off web task —
 * a running job timer or a client signing on the signature pad — and releases it when
 * the task ends. The web layer owns WHEN to hold (it knows the timer/signature state);
 * each platform owns the mechanism.
 *
 * Why it extends Strada's BridgeComponent: the iOS hold is reached via
 * this.send("acquire"/"release"), which only works on a connected bridge component
 * inside the iOS Hotwire Native shell. Extending BridgeComponent gives us that send()
 * transport AND the UA-gated connect() (it only connects in a native shell).
 *
 * Three-way transport via the window.ChippieBridge facade. Rather than each caller
 * branching on platform, this controller registers one keepAwake(on) impl set
 * ({ ios, android, pwa }); the facade (chippie-bridge.js) owns platform detection and
 * picks the right impl per call. Callers use window.ChippieBridge.keepAwake(true) to
 * hold and window.ChippieBridge.keepAwake(false) to release.
 *   - ios:     this.send("acquire") / this.send("release") over Strada to
 *              KeepAwakeComponent.swift (toggles UIApplication.isIdleTimerDisabled).
 *   - android: window.ChippieNative.setKeepAwake(on) (@JavascriptInterface; toggles
 *              the window FLAG_KEEP_SCREEN_ON on the UI thread).
 *   - pwa:     the Screen Wake Lock API (navigator.wakeLock.request("screen")),
 *              held in module state and released on demand. Re-acquired on
 *              visibilitychange while a hold is active, because the browser drops a
 *              wake lock whenever the tab is hidden.
 *
 * Android + PWA impls carry no per-instance state that needs a live bridge instance,
 * so they are registered once at module load via registerStandalone() — keepAwake()
 * works on those platforms even with no <element data-controller> on the page. The iOS
 * impl needs a live bridge instance for this.send, so connect() overrides it while the
 * controller is connected (i.e. inside the iOS shell) and disconnect() reverts (also
 * releasing any active hold, so a hold can never outlive the page that asked for it).
 *
 * Template wiring (the holding page mounts the controller so disconnect() can release):
 *   <div data-controller="bridge--keep-awake"> ...job timer / signature pad... </div>
 * Then from the timer/signature JS:
 *   window.ChippieBridge.keepAwake(true);   // on start
 *   window.ChippieBridge.keepAwake(false);  // on stop / save
 */
import { BridgeComponent } from "@hotwired/hotwire-native-bridge";

// PWA wake-lock state lives at module scope: a single sentinel held across calls,
// plus a flag tracking the caller's intent so we can re-acquire after the tab is
// hidden (the browser silently drops the lock on visibilitychange -> hidden).
let pwaWakeLock = null;
let pwaWanted = false;

function pwaAcquire() {
    if (!("wakeLock" in navigator)) return;
    if (pwaWakeLock) return; // already held
    navigator.wakeLock
        .request("screen")
        .then((sentinel) => {
            pwaWakeLock = sentinel;
            // The browser auto-releases on hidden/navigation; clear our handle so a
            // later re-acquire works and we never call release() on a dead sentinel.
            sentinel.addEventListener("release", () => { pwaWakeLock = null; });
        })
        .catch(() => { /* denied / unsupported — nothing to hold */ });
}

function pwaRelease() {
    if (!pwaWakeLock) return;
    try { pwaWakeLock.release(); } catch (_e) { /* already gone */ }
    pwaWakeLock = null;
}

function pwaSetKeepAwake(on) {
    pwaWanted = !!on;
    if (pwaWanted) pwaAcquire();
    else pwaRelease();
}

// Re-acquire when the tab returns to the foreground if a hold is still wanted —
// the wake lock is dropped whenever the document is hidden.
function onVisibilityChange() {
    if (pwaWanted && document.visibilityState === "visible") pwaAcquire();
}
document.addEventListener("visibilitychange", onVisibilityChange);

// Android + PWA impls carry no per-instance state, so register them at module load.
// The facade is the SSOT for which one runs per call.
function registerStandalone() {
    if (!window.ChippieBridge || !window.ChippieBridge.register) return;
    window.ChippieBridge.register("keepAwake", {
        // ios is overridden by the controller's connect() with a real send; this
        // placeholder is a safe no-op if somehow selected before connect.
        ios: () => {},
        android: (on) => {
            if (window.ChippieNative && window.ChippieNative.setKeepAwake) {
                try { window.ChippieNative.setKeepAwake(!!on); } catch (_e) { /* bridge gone */ }
            }
        },
        pwa: (on) => pwaSetKeepAwake(on),
    });
}

// Facade may load after this module; register as soon as both are present.
if (window.ChippieBridge) {
    registerStandalone();
} else {
    document.addEventListener("DOMContentLoaded", registerStandalone, { once: true });
}

export default class extends BridgeComponent {
    static component = "keep-awake";

    connect() {
        super.connect();
        // Inside the iOS shell now: wire the real Strada send into the facade,
        // keeping the Android/PWA fallbacks intact.
        if (!window.ChippieBridge || !window.ChippieBridge.register) return;
        window.ChippieBridge.register("keepAwake", {
            ios: (on) => this.send(on ? "acquire" : "release", {}),
            android: (on) => {
                if (window.ChippieNative && window.ChippieNative.setKeepAwake) {
                    try { window.ChippieNative.setKeepAwake(!!on); } catch (_e) { /* bridge gone */ }
                }
            },
            pwa: (on) => pwaSetKeepAwake(on),
        });
    }

    disconnect() {
        // Leaving the page that held the screen: release on every platform so a hold
        // can never outlive its page, then drop the live-send impl back to the
        // standalone placeholder so a stale this.send is never invoked.
        if (window.ChippieBridge && window.ChippieBridge.keepAwake) {
            try { window.ChippieBridge.keepAwake(false); } catch (_e) { /* facade gone */ }
        }
        registerStandalone();
        super.disconnect();
    }
}
